import { useEffect, useRef, useState } from 'react';
import {
  applyConsentPreferences,
  defaultConsentPreferences,
  readConsentPreferences,
  saveConsentPreferences,
  subscribeToCookiePreferenceOpens,
  type ConsentPreferences
} from '../lib/consent';

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [activePreferences, setActivePreferences] = useState<ConsentPreferences>(() => defaultConsentPreferences());
  const [editablePreferences, setEditablePreferences] = useState<ConsentPreferences>(() => defaultConsentPreferences());
  const [saveError, setSaveError] = useState<string | null>(null);
  const activePreferencesRef = useRef(activePreferences);
  const openerRef = useRef<HTMLElement | null>(null);
  const analyticsInputRef = useRef<HTMLInputElement>(null);

  const setActiveChoice = (preferences: ConsentPreferences) => {
    activePreferencesRef.current = preferences;
    setActivePreferences(preferences);
  };

  const closePreferences = () => {
    setVisible(false);
    setSaveError(null);
    const restoreOpenerFocus = () => openerRef.current?.focus();
    if (window.requestAnimationFrame) window.requestAnimationFrame(restoreOpenerFocus);
    else restoreOpenerFocus();
  };

  useEffect(() => {
    const stored = readConsentPreferences();
    if (stored) {
      setActiveChoice(stored);
      setEditablePreferences(stored);
      applyConsentPreferences(stored);
      return;
    }
    setVisible(true);
  }, []);

  useEffect(() => subscribeToCookiePreferenceOpens((event) => {
    const current = readConsentPreferences() ?? activePreferencesRef.current;
    setActiveChoice(current);
    setEditablePreferences(current);
    openerRef.current = event.detail.trigger ?? null;
    setSaveError(null);
    setVisible(true);
  }), []);

  useEffect(() => {
    if (!visible) return;
    const focusFirstSetting = () => analyticsInputRef.current?.focus();
    const frame = window.requestAnimationFrame?.(focusFirstSetting);
    if (frame === undefined) focusFirstSetting();
    return () => {
      if (frame !== undefined) window.cancelAnimationFrame?.(frame);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closePreferences();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [visible]);

  if (!visible) return null;

  const save = (analytics: boolean, marketing: boolean) => {
    const result = saveConsentPreferences({ analytics, marketing });
    if (!result.ok) {
      setEditablePreferences(activePreferences);
      setSaveError('We konden je cookievoorkeuren niet opslaan. Je vorige keuze blijft actief.');
      return;
    }
    setActiveChoice(result.preferences);
    setEditablePreferences(result.preferences);
    closePreferences();
  };

  return (
    <aside className="cookieBanner" role="dialog" aria-label="Cookie voorkeuren" aria-live="polite">
      <div>
        <p className="eyebrow">Privacy</p>
        <h2>Cookie voorkeuren</h2>
        <p>
          Noodzakelijke opslag houdt de site werkend. Analytics en marketing blijven uit totdat je ze toestaat.
        </p>
        {saveError ? <p className="cookieSaveError" role="status" aria-live="polite">{saveError}</p> : null}
      </div>
      <div className="cookieOptions">
        <label>
          <input type="checkbox" checked disabled />
          Noodzakelijk
        </label>
        <label>
          <input
            type="checkbox"
            ref={analyticsInputRef}
            checked={editablePreferences.analytics}
            onChange={(event) => setEditablePreferences({ ...editablePreferences, analytics: event.target.checked })}
          />
          Analytics
        </label>
        <label>
          <input
            type="checkbox"
            checked={editablePreferences.marketing}
            onChange={(event) => setEditablePreferences({ ...editablePreferences, marketing: event.target.checked })}
          />
          Marketing
        </label>
      </div>
      <div className="cookieActions">
        <button type="button" onClick={() => save(false, false)}>Weigeren</button>
        <button type="button" onClick={() => save(editablePreferences.analytics, editablePreferences.marketing)}>Opslaan</button>
        <button type="button" className="cookiePrimary" onClick={() => save(true, true)}>Accepteren</button>
      </div>
    </aside>
  );
}
